import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { isDevAuthEnabled, devSignIn } from '../utils/devAuth';
import { fetchSession } from '../services/auth';
import { toast } from 'sonner';

/**
 * VIP-Only Protected Route
 * Requires: is_admin=true OR (is_active=true AND plan='vip')
 * Non-VIP users are redirected to /projects with a toast message
 */
const VIPProtectedRoute = ({ children }) => {
    const [authState, setAuthState] = useState({ checking: true, authenticated: false, hasAccess: false });
    const location = useLocation();

    useEffect(() => {
        let mounted = true;

        const checkAuth = async () => {
            let token = localStorage.getItem('token');

            // Demo/dev mode: auto-provision a dev token
            if (isDevAuthEnabled() && !token) {
                devSignIn({ email: 'demo@digitalninja.app', name: 'Demo User' });
                token = localStorage.getItem('token');
            }

            // Dev auth: trust local token but check VIP status from localStorage
            if (isDevAuthEnabled() && token) {
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const hasAccess = user?.is_admin || (user?.is_active && user?.plan === 'vip');
                if (mounted) {
                    setAuthState({ checking: false, authenticated: true, hasAccess });
                }
                return;
            }

            // Try to fetch session from backend (cookie-based auth after Google OAuth)
            if (!token) {
                const user = await fetchSession(null);
                if (user) {
                    token = localStorage.getItem('token');
                    const hasAccess = user?.is_admin || (user?.is_active && user?.plan === 'vip');
                    if (mounted) {
                        setAuthState({ checking: false, authenticated: true, hasAccess });
                    }
                    return;
                }
            }

            if (!token) {
                if (mounted) {
                    setAuthState({ checking: false, authenticated: false, hasAccess: false });
                }
                return;
            }

            try {
                const response = await api.get('/auth/me');
                const user = response.data;
                // Store updated user data
                localStorage.setItem('user', JSON.stringify(user));

                // VIP check: is_admin OR (is_active AND plan='vip')
                const hasAccess = user?.is_admin || (user?.is_active && user?.plan === 'vip');

                if (mounted) {
                    setAuthState({ checking: false, authenticated: true, hasAccess });
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                if (mounted) {
                    setAuthState({ checking: false, authenticated: false, hasAccess: false });
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                }
            }
        };

        checkAuth();
        return () => { mounted = false; };
    }, [location.pathname]);

    if (authState.checking) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#1c1c1e]">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff4500]"></div>
                    <p className="text-white mt-4 text-lg">Loading...</p>
                </div>
            </div>
        );
    }

    if (!authState.authenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!authState.hasAccess) {
        // Show toast and redirect to projects (view-only mode)
        toast.error('VIP subscription required to access the AI Builder');
        return <Navigate to="/projects" state={{ from: location }} replace />;
    }

    return children;
};

export default VIPProtectedRoute;
